/**
 * web/src/lib/vault.ts — the sealed vault, client side (card t193KwD1i7aY, V3).
 *
 * Everything here runs in the BROWSER: the passphrase, the PBKDF2 derivation
 * and the root key never leave this module's callers. The worker stores wraps
 * and `pv:v1:` ciphertext and cannot open either — that split is the whole
 * point of the epic (spec V5SPOxJ3lCBq), so this file must never grow a
 * convenience that hands a passphrase or a root key to `api()`.
 *
 * Two shapes matter:
 *   - a WRAP is one member's copy of the couple root key, sealed under their
 *     own passphrase with their own salt (shared/vault-crypto.ts).
 *   - an ITEM is a letter or a secret, sealed under the root key with the item
 *     id as AAD, so a ciphertext cannot be re-homed to another row.
 */
import { api, getCoupleCode } from './api';
import {
  createVaultRootKey,
  generateVaultRecoveryCode,
  normalizeVaultRecoveryCode,
  openVaultItem,
  sealVaultItem,
  unwrapVaultRootKey,
  unwrapVaultRootKeyWithRecovery,
  wrapVaultRootKey,
  wrapVaultRootKeyWithRecovery,
} from '../../../shared/vault-crypto';
import type {
  VaultItem,
  VaultItemKind,
  VaultKeysView,
  VaultMemberWrapInput,
  VaultStoredRecoveryWrap,
} from '../../../shared/types';

/** What the screen renders: an item AFTER the client opened it. */
export interface VaultItemView {
  id: string;
  kind: VaultItemKind;
  title: string;
  body: string;
  createdAt: number;
  createdBy: string;
}

/** The plaintext we seal into `sealed`. JSON so a title survives beside a long
 *  letter body, and tolerant on read because an early item may be raw text. */
interface SealedBody {
  t: string;
  b: string;
}

function encodeBody(title: string, body: string): string {
  return JSON.stringify({ t: title, b: body } satisfies SealedBody);
}

function decodeBody(plaintext: string): { title: string; body: string } {
  try {
    const parsed = JSON.parse(plaintext) as Partial<SealedBody>;
    if (typeof parsed?.t === 'string' && typeof parsed?.b === 'string') {
      return { title: parsed.t, body: parsed.b };
    }
  } catch {
    /* not our JSON — fall through to the raw-text shape */
  }
  return { title: '', body: plaintext };
}

/** A member's wraps, never a key: this is safe to hold while LOCKED. */
export async function loadVaultKeys(): Promise<VaultKeysView | null> {
  const res = await api<{ keys: VaultKeysView | null }>('/api/vault/keys');
  return res.keys ?? null;
}

/** Store this member's wrap of the root key (idempotent: same slot replaced). */
export async function saveVaultWrap(input: VaultMemberWrapInput): Promise<VaultKeysView> {
  const res = await api<{ keys: VaultKeysView }>('/api/vault/keys', {
    body: { ...input },
  });
  return res.keys;
}

export async function listVaultItems(): Promise<VaultItem[]> {
  const res = await api<{ items: VaultItem[] }>('/api/vault');
  return res.items ?? [];
}

/**
 * Unlock with a passphrase. The client never learns which slot it owns, so it
 * tries each stored wrap: the one that opens is this member's (or a partner's
 * passphrase, entered deliberately on the join path). Every wrap failing is
 * `vault_bad_passphrase` — one honest answer for "wrong passphrase" and "not
 * enrolled yet", because the server cannot tell them apart either.
 */
export async function unlockRootKey(keys: VaultKeysView, passphrase: string): Promise<string> {
  for (const wrap of keys.wraps) {
    try {
      return await unwrapVaultRootKey(wrap, passphrase);
    } catch (e) {
      const code = e instanceof Error ? e.message : '';
      if (code === 'vault_bad_passphrase' || code === 'vault_bad_wrap') continue;
      throw e;
    }
  }
  throw new Error('vault_bad_passphrase');
}

/**
 * First enrolment: mint the couple root key, wrap it under a passphrase, and
 * issue the recovery code (V5). The recovery wrap is a THIRD wrap of the same
 * root key under HKDF(code, coupleId) — the server stores it and cannot open it.
 * The code is returned once and never stored anywhere client-side: if the member
 * loses it, the partner's passphrase (or the code) is what remains.
 */
export async function createVault(
  passphrase: string,
): Promise<{ rootKey: string; recoveryCode: string }> {
  const rootKey = createVaultRootKey();
  await saveVaultWrap(await wrapVaultRootKey(rootKey, passphrase));
  let recoveryCode = '';
  try {
    recoveryCode = await issueRecoveryCode(rootKey);
  } catch {
    // The vault exists either way; a failed code is reported, never invented.
    throw new Error('vault_recovery_not_saved');
  }
  return { rootKey, recoveryCode };
}

/** The couple id the recovery KEK is salted with (`c_<code>`, server-issued). */
export function vaultCoupleId(): string {
  const code = getCoupleCode();
  if (!code) throw new Error('vault_no_couple');
  return `c_${code}`;
}

/**
 * Mint a fresh recovery code, wrap the root key under it and store the wrap.
 * Called at enrolment and available later for a vault that was created before
 * V5 — re-issuing replaces the previous code, so the old one stops working.
 */
export async function issueRecoveryCode(rootKey: string): Promise<string> {
  const code = generateVaultRecoveryCode();
  const wrap = await wrapVaultRootKeyWithRecovery(rootKey, code, vaultCoupleId());
  await api<{ keys: VaultKeysView }>('/api/vault/recovery', { body: { ...wrap } });
  return code;
}

/** Open the root key with the recovery code alone (passphrase lost). */
export async function unlockWithRecoveryCode(
  keys: VaultKeysView,
  recoveryCode: string,
): Promise<string> {
  if (!keys.recovery) throw new Error('vault_no_recovery');
  const wrap: VaultStoredRecoveryWrap = keys.recovery;
  return unwrapVaultRootKeyWithRecovery(wrap, recoveryCode, vaultCoupleId());
}

/**
 * Recovery, end to end: open the root key with the code, then re-wrap it under
 * a NEW passphrase. The old passphrase stops working because its wrap is
 * replaced — that is the point, and it is why this is one call.
 */
export async function recoverVault(
  keys: VaultKeysView,
  recoveryCode: string,
  newPassphrase: string,
): Promise<string> {
  const rootKey = await unlockWithRecoveryCode(keys, recoveryCode);
  await saveVaultWrap(await wrapVaultRootKey(rootKey, newPassphrase));
  return rootKey;
}

/**
 * Partner re-issue: an UNLOCKED member wraps the same root key for the other
 * member's new passphrase. The server refuses a slot that has no wrap yet, so
 * this resets a partner who is enrolled; it never enrols one on their behalf.
 */
export async function reissuePartnerWrap(
  rootKey: string,
  slot: 'a' | 'b',
  newPassphrase: string,
): Promise<VaultKeysView> {
  const wrap = await wrapVaultRootKey(rootKey, newPassphrase);
  return saveVaultWrapSlot(wrap, slot);
}

/** Store a wrap in a specific slot (partner re-issue only). */
async function saveVaultWrapSlot(
  input: VaultMemberWrapInput,
  slot: 'a' | 'b',
): Promise<VaultKeysView> {
  const res = await api<{ keys: VaultKeysView }>('/api/vault/keys', {
    body: { ...input, slot },
  });
  return res.keys;
}

/** True when a stored code looks well-formed — the UI checks before sending. */
export function looksLikeRecoveryCode(code: string): boolean {
  try {
    normalizeVaultRecoveryCode(code);
    return true;
  } catch {
    return false;
  }
}

/**
 * Join an existing vault: unwrap the SAME root key with the passphrase that
 * already opens it, then wrap it under this member's own passphrase. The
 * out-of-band step (a partner telling you the vault passphrase once) is V3
 * scope; V5's recovery code replaces it.
 */
export async function joinVault(
  keys: VaultKeysView,
  existingPassphrase: string,
  ownPassphrase: string,
): Promise<string> {
  const rootKey = await unlockRootKey(keys, existingPassphrase);
  await saveVaultWrap(await wrapVaultRootKey(rootKey, ownPassphrase));
  return rootKey;
}

/** Seal and store one entry. The id is minted first because it is the AAD. */
export async function addVaultItem(
  rootKey: string,
  kind: VaultItemKind,
  title: string,
  body: string,
): Promise<VaultItem> {
  const id = (globalThis.crypto?.randomUUID?.() ?? `v${Date.now()}${Math.random()}`).replace(/[^A-Za-z0-9_-]/g, '');
  const sealed = await sealVaultItem(rootKey, encodeBody(title, body), id);
  const res = await api<{ item: VaultItem }>('/api/vault', {
    body: { id, kind, sealed },
  });
  return res.item;
}

/* -------------------------------------------------------------------------
   Photos (card UAJ0OVm6tmJw, V4). A photo is never sent as the file the phone
   produced: it is drawn to a canvas, downscaled to PHOTO_MAX_SIDE, encoded as
   JPEG and sealed exactly like a letter body. What leaves the device is
   `pv:v1:` ciphertext; the bytes behind it have already been shrunk so the
   sealed row fits the server's VAULT_PHOTO_SEALED_MAX cap.
   ------------------------------------------------------------------------- */

/** Longest edge kept. Big enough for a printed memory, small enough to seal. */
export const PHOTO_MAX_SIDE = 1280;
/** Mirrors durable-object/vault.ts — the server refuses above this. */
export const PHOTO_SEALED_MAX = 900_000;
/** How many photos one couple may keep; the picker refuses a batch past it. */
export const PHOTO_MAX_COUNT = 60;
/** Refuse absurd files before touching the decoder. */
const PHOTO_INPUT_MAX_BYTES = 25 * 1024 * 1024;

/** What a sealed data URL costs once sealed: plaintext → ciphertext → base64. */
function sealedLengthFor(plaintextLength: number): number {
  return Math.ceil((plaintextLength + 28) / 3) * 4 + 'pv:v1:'.length;
}

function drawToJpeg(source: CanvasImageSource, width: number, height: number, quality: number): string {
  const canvas = document.createElement('canvas');
  const scale = Math.min(1, PHOTO_MAX_SIDE / Math.max(width, height));
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('vault_photo_failed');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL('image/jpeg', quality);
}

/**
 * Read a picked file into a sealed-sized JPEG data URL. Quality steps down
 * before anything else does, because a soft photo beats a rejected photo; only
 * a picture that will not fit even at the lowest quality fails, and then it
 * fails with a sentence rather than a half-written row.
 */
export async function preparePhoto(file: File): Promise<string> {
  if (!file.type.startsWith('image/')) throw new Error('vault_photo_type');
  if (file.size > PHOTO_INPUT_MAX_BYTES) throw new Error('vault_photo_too_large');
  let bitmap: ImageBitmap | null = null;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    bitmap = null;
  }
  if (bitmap) {
    try {
      for (const quality of [0.82, 0.68, 0.55, 0.42]) {
        const dataUrl = drawToJpeg(bitmap, bitmap.width, bitmap.height, quality);
        if (sealedLengthFor(dataUrl.length) <= PHOTO_SEALED_MAX) return dataUrl;
      }
    } finally {
      bitmap.close();
    }
    throw new Error('vault_photo_too_large');
  }
  throw new Error('vault_photo_failed');
}

/** Seal a photo the caller already prepared (the screen previews the same
 *  string it seals, so the preview cannot drift from what is stored). */
export async function sealVaultPhoto(
  rootKey: string,
  title: string,
  dataUrl: string,
): Promise<VaultItem> {
  if (!dataUrl.startsWith('data:image/')) throw new Error('vault_photo_failed');
  const id = (globalThis.crypto?.randomUUID?.() ?? `p${Date.now()}${Math.random()}`).replace(/[^A-Za-z0-9_-]/g, '');
  const sealed = await sealVaultItem(rootKey, encodeBody(title, dataUrl), id);
  if (sealed.length > PHOTO_SEALED_MAX) throw new Error('vault_photo_too_large');
  const res = await api<{ item: VaultItem }>('/api/vault', {
    body: { id, kind: 'photo', sealed },
  });
  return res.item;
}

/** Pick → prepare → seal, for callers that only hold the File. */
export async function addVaultPhoto(
  rootKey: string,
  file: File,
  title: string,
): Promise<VaultItem> {
  return sealVaultPhoto(rootKey, title, await preparePhoto(file));
}

export async function removeVaultItem(id: string): Promise<boolean> {
  const res = await api<{ deleted: boolean }>(`/api/vault/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
  return Boolean(res.deleted);
}

/** Open every row this root key can read. A row that will not open is shown as
 *  unreadable rather than dropped — a silent gap reads like data loss. */
export async function openVaultItems(rootKey: string, items: VaultItem[]): Promise<VaultItemView[]> {
  const out: VaultItemView[] = [];
  for (const item of items) {
    const kind: VaultItemKind =
      item.kind === 'secret' ? 'secret' : item.kind === 'photo' ? 'photo' : 'letter';
    let title = '';
    let body = 'This entry could not be opened with this vault key.';
    try {
      const decoded = decodeBody(await openVaultItem(rootKey, item.sealed, item.id));
      title = decoded.title;
      body = decoded.body;
    } catch {
      /* keep the honest placeholder */
    }
    out.push({
      id: item.id,
      kind,
      title,
      body,
      createdAt: item.createdAt,
      createdBy: item.createdBy,
    });
  }
  return out.sort((a, b) => b.createdAt - a.createdAt);
}

/** Plain-language failure copy. Never echoes server text or key material. */
export function vaultErrorMessage(e: unknown): string {
  const raw = e instanceof Error ? e.message : '';
  const code = /vault_[a-z_]+/.exec(raw)?.[0] ?? '';
  if (code === 'vault_bad_passphrase') return 'That passphrase does not match this vault.';
  if (code === 'vault_no_passphrase') return 'Enter a passphrase to continue.';
  if (code === 'vault_bad_recovery_code') return 'That recovery code does not open this vault.';
  if (code === 'vault_no_recovery') return 'This vault has no recovery code yet — ask your partner to reset your passphrase.';
  if (code === 'vault_recovery_not_saved')
    return 'Your vault is set up, but the recovery code could not be saved. Open the vault and issue one.';
  if (code === 'vault_no_couple') return 'Open the vault from your shared space to use recovery.';
  if (raw.includes('vault_slot_empty') || raw.includes('no vault yet'))
    return 'Your partner has not set up the vault yet.';
  if (raw.includes('vault_photo_limit'))
    return 'This vault is full for photos — delete one to add another.';
  if (raw.includes('vault_photo_type')) return 'That file is not a photo.';
  if (raw.includes('vault_photo_too_large')) return 'That photo is too large to seal — try a smaller one.';
  if (raw.includes('payload_too_large') || /too large/i.test(raw)) return 'That entry is too long to save.';
  if (raw.includes('bad_payload')) return 'That entry could not be saved.';
  return 'The vault could not be reached just now. Nothing you typed was lost — try again.';
}
